import { WarningIcon } from "@phosphor-icons/react";
import { isAxiosError } from "axios";
import { useIntl, type IntlShape } from "react-intl";

import { ReservationType } from "@/features/bookings/types/types";
import { type ApiError } from "@/types/types";

import PageError, { type PageErrorProps } from "./PageError";

interface Props {
  type: "flights" | "hotels";
  error: unknown;
}

const getErrorPageContent = ({
  type,
  error,
  intl,
}: Props & { intl: IntlShape }) => {
  let title =
    type === "flights"
      ? intl.formatMessage({ id: "flights.errorWhileBooking" })
      : intl.formatMessage({ id: "hotels.errorWhileBooking" });
  let subtitles =
    type === "flights"
      ? [intl.formatMessage({ id: "flights.errorWhileBookingDesc" })]
      : [intl.formatMessage({ id: "hotels.errorWhileBookingDesc" })];
  let back: PageErrorProps["back"] = {
    text: intl.formatMessage({ id: "backToHome" }),
    options: { to: type === "flights" ? "/flights/search" : "/hotels/search" },
  };

  if (!(error instanceof Error)) {
    title = intl.formatMessage({ id: "paymentError" });
    subtitles = [intl.formatMessage({ id: "paymentErrorDesc" })];
  }

  if (
    isAxiosError<ApiError>(error) &&
    error.response?.data.code === "VALIDATION_FAILED"
  ) {
    subtitles = [];
    back = { text: intl.formatMessage({ id: "back" }) };
    error.response?.data.data.forEach((validationError) => {
      subtitles.push(validationError.message);
    });
  }

  return { title, subtitles, back };
};

export const BookingError = ({ error, type }: Props) => {
  const intl = useIntl();

  const { title, subtitles, back } = getErrorPageContent({ error, type, intl });

  return (
    <PageError
      title={title}
      subtitles={subtitles}
      back={{ ...back }}
      link={{
        text: intl.formatMessage({ id: "orderDetails" }),
        options: {
          to: "/bookings",
          search: { category: type, reservationType: ReservationType.REFUNDED },
        },
      }}
      icon={<WarningIcon size={60} color="#de370c" />}
    />
  );
};
